import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { initializeAllConsentPreferences } from '../../components/CookieConsent/utils/cookieUtils';

export interface CookieCategory {
  id: string;
  name: string;
  description: string;
  required: boolean;
  cookies: string[];
}

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface CookieConsentState {
  hasUserConsented: boolean;
  showConsentBanner: boolean;
  showSettingsModal: boolean;
  preferences: CookiePreferences;
  lastConsentDate: string | null;
}

const initialCookiePreferences: CookiePreferences = {
  necessary: true, // Always true, cannot be disabled
  analytics: false,
  marketing: false,
  preferences: false,
};

const getStoredCookieConsent = (): Partial<CookieConsentState> => {
  try {
    const stored = localStorage.getItem('cookieConsent');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to parse stored cookie consent:', error);
  }
  return {};
};

const shouldShowConsentBanner = (): boolean => {
  const stored = getStoredCookieConsent();
  return !stored.hasUserConsented;
};

const initialState: CookieConsentState = {
  hasUserConsented: false,
  showConsentBanner: shouldShowConsentBanner(),
  showSettingsModal: false,
  preferences: initialCookiePreferences,
  lastConsentDate: null,
  ...getStoredCookieConsent(),
};

if (initialState.hasUserConsented) {
  initializeAllConsentPreferences(initialState.preferences);
}

const cookieConsentSlice = createSlice({
  name: 'cookieConsent',
  initialState,
  reducers: {
    acceptAllCookies: (state) => {
      state.hasUserConsented = true;
      state.showConsentBanner = false;
      state.showSettingsModal = false;
      state.preferences = {
        necessary: true,
        analytics: true,
        marketing: true,
        preferences: true,
      };
      state.lastConsentDate = new Date().toISOString();
      localStorage.setItem('cookieConsent', JSON.stringify(state));

      initializeAllConsentPreferences(state.preferences);
    },

    declineNonEssentialCookies: (state) => {
      state.hasUserConsented = true;
      state.showConsentBanner = false;
      state.showSettingsModal = false;
      state.preferences = {
        necessary: true,
        analytics: false,
        marketing: false,
        preferences: false,
      };
      state.lastConsentDate = new Date().toISOString();
      localStorage.setItem('cookieConsent', JSON.stringify(state));

      initializeAllConsentPreferences(state.preferences);
    },

    updateCookiePreferences: (
      state,
      action: PayloadAction<Partial<CookiePreferences>>,
    ) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
        necessary: true, // Always keep necessary cookies enabled
      };
      state.hasUserConsented = true;
      state.showConsentBanner = false;
      state.showSettingsModal = false;
      state.lastConsentDate = new Date().toISOString();
      localStorage.setItem('cookieConsent', JSON.stringify(state));

      initializeAllConsentPreferences(state.preferences);
    },

    openCookieSettings: (state) => {
      state.showSettingsModal = true;
    },

    closeCookieSettings: (state) => {
      state.showSettingsModal = false;
    },

    resetCookieConsent: (state) => {
      const resetState = {
        ...initialState,
        showConsentBanner: true,
        hasUserConsented: false,
        lastConsentDate: null,
      };
      Object.assign(state, resetState);
      localStorage.removeItem('cookieConsent');
    },
  },
});

export const {
  acceptAllCookies,
  declineNonEssentialCookies,
  updateCookiePreferences,
  openCookieSettings,
  closeCookieSettings,
  resetCookieConsent,
} = cookieConsentSlice.actions;

export default cookieConsentSlice.reducer;
